import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Brain,
  ClipboardList,
  FileText,
  TrendingUp,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import useAssessmentData from "../hooks/useAssessmentData";
import ResourcePersonNavBar from "../components/ResourcePersonNavBar";

const metric = (value, suffix = "") =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? "—"
    : `${Number(value).toLocaleString()}${suffix}`;

const avg = (values) => {
  const list = values.map(Number).filter((n) => Number.isFinite(n));
  if (!list.length) return null;
  return Math.round((list.reduce((sum, n) => sum + n, 0) / list.length) * 10) / 10;
};

const kindOf = (submission) =>
  String(submission?.assessment?.kind || submission?.kind || submission?.type || "").toUpperCase();

const shortDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const money = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount)
    ? new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: "NGN",
        maximumFractionDigits: 0,
      }).format(amount)
    : "—";
};

const ACCENTS = {
  assignments: {
    bg: "bg-blue-50/70",
    icon: "bg-gradient-to-br from-blue-600 to-cyan-500",
    text: "text-blue-700",
    chip: "bg-blue-50 text-blue-700 ring-blue-100",
  },
  reports: {
    bg: "bg-emerald-50/70",
    icon: "bg-gradient-to-br from-emerald-600 to-teal-500",
    text: "text-emerald-700",
    chip: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  },
  quizzes: {
    bg: "bg-violet-50/70",
    icon: "bg-gradient-to-br from-violet-600 to-fuchsia-500",
    text: "text-violet-700",
    chip: "bg-violet-50 text-violet-700 ring-violet-100",
  },
  students: {
    bg: "bg-amber-50/70",
    icon: "bg-gradient-to-br from-amber-500 to-orange-500",
    text: "text-amber-700",
    chip: "bg-amber-50 text-amber-700 ring-amber-100",
  },
};

export default function Assessment() {
  const [assessments, setAssessments] = useState([]);
  const [assessmentError, setAssessmentError] = useState("");
  const [assessmentLoading, setAssessmentLoading] = useState(false);

  const {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    selectedCourse,
    lessons,
    lessonsCourseId,
    courseStudents,
    courseAnalytics,
    submissions,
    earningsSummary,
    loadAssessments,
  } = useAssessmentData();

  // Lessons list may not include nested assessments on every backend build, so
  // load them through the documented nested endpoint (same as QuizzesPage).
  useEffect(() => {
    let cancelled = false;
    setAssessments([]);
    setAssessmentError("");
    if (
      !selectedCourseId ||
      String(lessonsCourseId) !== String(selectedCourseId) ||
      !lessons.length
    ) {
      return () => {
        cancelled = true;
      };
    }

    setAssessmentLoading(true);
    Promise.all(
      lessons.map(async (lesson) => {
        const result = await loadAssessments(selectedCourseId, lesson.id);
        if (!result.success) {
          throw new Error(result.error || "Could not load lesson assessments.");
        }
        return (result.assessments || []).map((assessment) => ({
          ...assessment,
          lessonId: lesson.id,
          lessonTitle: lesson.title,
        }));
      })
    )
      .then((groups) => {
        if (!cancelled) setAssessments(groups.flat());
      })
      .catch((error) => {
        if (!cancelled) setAssessmentError(error.message);
      })
      .finally(() => {
        if (!cancelled) setAssessmentLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCourseId, lessonsCourseId, lessons, loadAssessments]);

  const assignmentAssessments = assessments.filter(
    (assessment) => String(assessment.kind || "").toUpperCase() === "ASSIGNMENT"
  );
  const quizAssessments = assessments.filter(
    (assessment) => String(assessment.kind || "").toUpperCase() === "QUIZ"
  );

  const assignmentSubmissions = submissions.filter(
    (submission) => kindOf(submission) === "ASSIGNMENT"
  );
  const quizSubmissions = submissions.filter((submission) => kindOf(submission) === "QUIZ");

  const graded = (items) => items.filter((item) => item.status === "GRADED").length;
  const pending = (items) => items.filter((item) => item.status !== "GRADED").length;

  const students = useMemo(() => {
    const list = Array.isArray(courseStudents) ? courseStudents : [];
    const withProgress = list.map((student) => ({
      ...student,
      progress: Number(student.progress) || 0,
      completed: Boolean(student.completed),
    }));
    const completed = withProgress.filter((student) => student.completed).length;
    return {
      list: withProgress,
      total: withProgress.length,
      completed,
      averageProgress:
        courseAnalytics?.averageProgress != null
          ? Number(courseAnalytics.averageProgress)
          : avg(withProgress.map((student) => student.progress)),
    };
  }, [courseStudents, courseAnalytics]);

  const analytics = courseAnalytics || {};
  const coursesList = Array.isArray(courses) ? courses : [];

  const cards = [
    {
      key: "assignments",
      title: "Assignments",
      icon: ClipboardList,
      description: "Learner submissions across this course",
      route: "/resource-person/assessment/assignments",
      count: assignmentAssessments.length,
      done: graded(assignmentSubmissions),
      open: pending(assignmentSubmissions),
      progress:
        assignmentSubmissions.length > 0
          ? (graded(assignmentSubmissions) / assignmentSubmissions.length) * 100
          : 0,
      stats: [
        { label: "Assignments", value: metric(assignmentAssessments.length) },
        { label: "Submissions", value: metric(assignmentSubmissions.length) },
        { label: "Graded", value: metric(graded(assignmentSubmissions)) },
        { label: "Awaiting grading", value: metric(pending(assignmentSubmissions)) },
      ],
      recent: assignmentSubmissions.slice(0, 3).map((submission) => ({
        title:
          submission.assessment?.title ||
          submission.lesson?.title ||
          submission.lessonTitle ||
          "Assignment submission",
        meta: `${submission.learner?.name || "Learner"} · ${shortDate(
          submission.submittedAt
        )}`,
        done: submission.status === "GRADED",
      })),
      empty: "No assignment submissions yet for this course.",
    },
    {
      key: "reports",
      title: "Reports",
      icon: FileText,
      description: "Course performance and earnings at a glance",
      route: "/resource-person/assessment/reports",
      count: analytics.totalEnrollments ?? 0,
      done: analytics.completionRate ?? 0,
      open: null,
      progress: Number(analytics.completionRate) || 0,
      stats: [
        { label: "Enrollments", value: metric(analytics.totalEnrollments) },
        { label: "Revenue", value: money(analytics.totalRevenue) },
        { label: "Avg progress", value: metric(analytics.averageProgress, "%") },
        { label: "Completion", value: metric(analytics.completionRate, "%") },
      ],
      recent: (analytics.recentPurchases || []).slice(0, 3).map((purchase) => ({
        title: purchase.learnerName || "Learner purchase",
        meta: `${money(purchase.amount)} · ${shortDate(purchase.purchasedAt)}`,
        done: true,
      })),
      empty: "No purchases yet for this course.",
    },
    {
      key: "quizzes",
      title: "Quizzes",
      icon: Brain,
      description: "Quiz assessments and their submissions",
      route: "/resource-person/assessment/quizzes",
      count: quizAssessments.length,
      done: graded(quizSubmissions),
      open: pending(quizSubmissions),
      progress:
        quizSubmissions.length > 0
          ? (graded(quizSubmissions) / quizSubmissions.length) * 100
          : 0,
      stats: [
        { label: "Quizzes", value: metric(quizAssessments.length) },
        { label: "Submissions", value: metric(quizSubmissions.length) },
        { label: "Graded", value: metric(graded(quizSubmissions)) },
        { label: "Awaiting grading", value: metric(pending(quizSubmissions)) },
      ],
      recent: quizSubmissions.slice(0, 3).map((submission) => ({
        title:
          submission.assessment?.title ||
          submission.lesson?.title ||
          submission.lessonTitle ||
          "Quiz submission",
        meta: `${submission.learner?.name || "Learner"} · ${shortDate(
          submission.submittedAt
        )}`,
        done: submission.status === "GRADED",
      })),
      empty: "No quiz submissions yet for this course.",
    },
    {
      key: "students",
      title: "Student Progress",
      icon: TrendingUp,
      description: "Enrolled learners and completion",
      route: "/resource-person/assessment/students",
      count: students.total,
      done: students.completed,
      open: Math.max(0, students.total - students.completed),
      progress: Number(students.averageProgress) || 0,
      stats: [
        { label: "Students", value: metric(students.total) },
        { label: "Completed", value: metric(students.completed) },
        { label: "In progress", value: metric(students.total - students.completed) },
        { label: "Avg progress", value: metric(students.averageProgress, "%") },
      ],
      recent: students.list.slice(0, 3).map((student) => ({
        title: student.name || "Learner",
        meta: `${student.progress}% progress · ${student.completed ? "Completed" : "In progress"}`,
        done: student.completed,
      })),
      empty: "No learners enrolled in this course yet.",
    },
  ];

  const accountEarnings = earningsSummary || {};
  const totalCourses = coursesList.length;

  return (
    <div className="min-h-screen bg-slate-50">
      <ResourcePersonNavBar title="Assessment" subTitle="Assessment Center" />

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-6">
        {/* Header */}
        <section className="flex flex-col gap-5 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-[#004ac6]">
              Assessment Center
            </p>
            <h1 className="m-0 mt-2 text-3xl font-bold tracking-tight text-slate-900">
              Assessments & learner progress
            </h1>
            <p className="m-0 mt-2 max-w-2xl text-sm text-slate-500">
              Live counts for assignments, quizzes, reports and learners. Choose a course to
              scope every card.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">
              {totalCourses} course{totalCourses === 1 ? "" : "s"}
            </span>
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
              {money(accountEarnings.lifetime ?? accountEarnings.available)} lifetime
            </span>
          </div>
        </section>

        {(coursesError || assessmentError) && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {coursesError || assessmentError}
          </div>
        )}

        {/* Course picker */}
        {coursesLoading ? (
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
            Loading your courses…
          </div>
        ) : coursesList.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <p className="m-0 text-sm font-semibold text-slate-700">No courses yet</p>
            <p className="m-0 mt-1 text-sm text-slate-500">
              Create a course and add lessons before using the Assessment Center.
            </p>
            <Link to="/resource-person/content-library/course/new">
              <Button className="mt-4 bg-[#16213A] text-white hover:bg-[#203052]">
                Create a course
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <label className="text-sm font-semibold text-slate-700" htmlFor="assessment-course">
                Course
              </label>
              <select
                id="assessment-course"
                value={selectedCourseId}
                onChange={(event) => setSelectedCourseId(event.target.value)}
                className="min-w-[240px] flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-800 outline-none focus:border-[#16213A] focus:ring-2 focus:ring-[#16213A]/10 sm:flex-none"
              >
                {coursesList.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
              <span className="text-xs text-slate-500">
                {selectedCourse?.status ? `Status: ${selectedCourse.status}` : "Live course data"}
                {assessmentLoading ? " · loading assessments…" : ""}
              </span>
            </div>

            {/* Cards — the whole card is a link to its page */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {cards.map((card) => {
                const Icon = card.icon;
                const theme = ACCENTS[card.key];
                const newCount = card.open != null ? card.open : card.count;
                const badgeLabel =
                  card.key === "students"
                    ? newCount > 0
                      ? "open"
                      : "total"
                    : card.key === "reports"
                      ? "total"
                      : newCount > 0
                        ? "new"
                        : "total";
                return (
                  <Link key={card.key} to={card.route} className="group block">
                    <Card
                      className={`h-full overflow-hidden rounded-2xl border border-slate-200 shadow-sm transition duration-200 group-hover:-translate-y-0.5 group-hover:shadow-lg`}
                    >
                      <CardContent className={`space-y-4 p-5 ${theme.bg}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span
                              className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg ${theme.icon}`}
                            >
                              <Icon className="h-6 w-6" />
                            </span>
                            <div className="min-w-0">
                              <h3 className="m-0 text-lg font-bold text-slate-900">{card.title}</h3>
                              <p className="m-0 mt-0.5 text-sm text-slate-600">
                                {card.description}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${theme.chip}`}
                            title="Items needing attention"
                          >
                            {metric(newCount)} {badgeLabel}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs font-semibold text-slate-600">
                            <span>{card.key === "reports" ? "Completion" : "Progress"}</span>
                            <span>{Math.round(card.progress || 0)}%</span>
                          </div>
                          <Progress value={card.progress || 0} className="h-2" />
                          <div className="flex items-center justify-between text-xs text-slate-500">
                            <span>{metric(card.count)} total</span>
                            <span>
                              {metric(card.done)} done
                              {card.open != null ? ` · ${metric(card.open)} open` : ""}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                          {card.stats.map((stat) => (
                            <div
                              key={stat.label}
                              className="rounded-xl border border-white/70 bg-white/70 px-3 py-2 backdrop-blur"
                            >
                              <p className="m-0 text-[11px] text-slate-500">{stat.label}</p>
                              <p className="m-0 mt-0.5 text-sm font-bold text-slate-900">
                                {stat.value}
                              </p>
                            </div>
                          ))}
                        </div>

                        <div className="flex items-center justify-between border-t border-white/60 pt-3 text-sm font-semibold text-slate-700">
                          <span>Open {card.title}</span>
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
